// REL_1.1.14 F-262 / S-981 — Ported from deedo-frontend ForAgentsPage.tsx (REL_1.1.12 design).
// Differences:
//   - "Claim Your Agent Account" CTA cross-domains to app.deedo.ai/signup (was internal /signup)
//   - Canonical URL: /for-agents (REL_1.1.15 S-1101 v2.8 — `/us/` prefix dropped)
import { Helmet } from "react-helmet-async";
import { Building2, MessageSquare, BarChart3 } from "lucide-react";

const MARKETING_BASE = import.meta.env.VITE_MARKETING_BASE_URL || "https://www.deedo.ai";
const APP_BASE_URL: string = import.meta.env.VITE_APP_BASE_URL || "https://app.deedo.ai";

const FEATURES = [
  {
    title: "Deedo Print Agent",
    Icon: Building2,
    iconColor: "text-[var(--brand-primary)]",
    body: "Instantly generate beautifully branded, print-ready QR flyers for every listing. Our engine automatically centers a high-res QR code, applies your branding, and outputs a 300 DPI PDF ready for the sign yard.",
  },
  {
    title: "24/7 AI Assistant",
    Icon: MessageSquare,
    iconColor: "text-[var(--brand-secondary)]",
    body: "Your AI co-pilot answers routine buyer questions instantly. Feed it your MLS data and disclosures, and let it handle the “how old is the HVAC?” questions while you focus on building relationships.",
  },
  {
    title: "Smart Lead Generation",
    Icon: BarChart3,
    iconColor: "text-[var(--brand-accent)]",
    body: "Capture high-intent leads passively. When buyers engage with the AI to ask questions or request documents, their contact info is routed directly to your dashboard.",
  },
];

const ONBOARDING_STEPS = [
  { n: "1", title: "Claim Account",  body: "Sign up and verify your real estate credentials in minutes." },
  { n: "2", title: "Upload Listing", body: "Add property details, MLS data, and documents to train the AI instantly." },
  { n: "3", title: "Print & Deploy", body: "Generate your Deedo Print flyer and place it at your open house or yard sign." },
];

export default function ForAgents() {
  return (
    <>
      <Helmet>
        <title>For Agents — Deedo</title>
        <meta
          name="description"
          content="Supercharge your open houses and automate lead gen. Generate print-ready QR flyers, deploy an AI co-pilot, and capture high-intent buyer leads passively."
        />
        <link rel="canonical" href={`${MARKETING_BASE}/for-agents`} />
        <meta property="og:title" content="For Agents — Deedo" />
        <meta property="og:description" content="Print-ready QR flyers + AI co-pilot + lead capture for real estate professionals." />
        <meta property="og:url" content={`${MARKETING_BASE}/for-agents`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="relative bg-gradient-to-br from-[var(--brand-primary)] via-[var(--brand-primary-light)] to-[var(--brand-primary)] text-white">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center lg:px-8 lg:py-24">
          <span className="inline-flex items-center rounded-full bg-[var(--brand-secondary)] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[var(--brand-primary)]">
            For Real Estate Professionals
          </span>
          <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight lg:text-5xl">
            Supercharge Your Open Houses
            <br />
            &amp; Automate Lead Gen
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-blue-100">
            Generate print-ready QR flyers, deploy an AI co-pilot, and capture high-intent buyer leads passively.
          </p>
          <a
            href={`${APP_BASE_URL}/signup`}
            className="mt-8 inline-block rounded-lg bg-[var(--brand-secondary)] px-7 py-3 text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-secondary-light)]"
            aria-label="Claim your agent account"
          >
            Claim Your Agent Account
          </a>
        </div>
      </section>

      <section id="deedo-print" className="bg-gray-50 py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {FEATURES.map(({ title, Icon, iconColor, body }) => (
              <article key={title} className="rounded-2xl bg-white p-7 shadow-sm">
                <Icon className={`h-9 w-9 ${iconColor}`} aria-hidden="true" />
                <h3 className="mt-6 text-lg font-bold text-[var(--brand-primary)]">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{body}</p>
              </article>
            ))}
          </div>

          <div className="mt-24 text-center">
            <h2 className="text-3xl font-bold text-[var(--brand-primary)] lg:text-4xl">
              Onboarding in 3 Simple Steps
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {ONBOARDING_STEPS.map(({ n, title, body }, idx) => (
              <div key={n} className="relative">
                {idx < ONBOARDING_STEPS.length - 1 && (
                  <div
                    className="absolute right-0 top-12 hidden h-px w-6 -translate-y-1/2 translate-x-3 bg-gray-200 md:block"
                    aria-hidden="true"
                  />
                )}
                <article className="rounded-2xl bg-white p-7 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand-primary)]">
                    <span className="text-xl font-bold text-white">{n}</span>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-[var(--brand-primary)]">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{body}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
